/**
 * Guidance Library Component
 * Displays official resources, guides, and framework information
 */
import React from 'react';
import { ExternalLink, FileText, BookOpen, AlertTriangle } from 'lucide-react';

export default function GuidanceLibrary({ framework }) {
  return (
    <div className="space-y-6" data-testid="guidance-library">
      {/* Official Resources */}
      <div className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <BookOpen size={20} className="text-blue-400" />
          Official Resources
        </h3>

        <div className="space-y-3">
          <ResourceLink
            title="ESRS Standards (Full Set)"
            description="Complete European Sustainability Reporting Standards"
            url="https://www.efrag.org/lab6"
            type="EFRAG"
          />
          <ResourceLink
            title="CSRD Directive Text"
            description="Official EU directive establishing sustainability reporting requirements"
            url="https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:32022L2464"
            type="EUR-Lex"
          />
          <ResourceLink
            title="Implementation Guidance"
            description="Practical guidance for implementing ESRS requirements"
            url="https://www.efrag.org/Activities/2010051123028442"
            type="EFRAG"
          />
          <ResourceLink
            title="XBRL Taxonomy"
            description="ESRS XBRL digital reporting taxonomy"
            url="https://www.efrag.org/lab6/esrs-digital-taxonomy"
            type="EFRAG"
          />
        </div>
      </div>

      {/* Implementation Guides */}
      <div className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <FileText size={20} className="text-emerald-400" />
          Implementation Guides
        </h3>

        <div className="space-y-3">
          <ResourceLink
            title="ESRS Set 1 - Full Standards"
            description="All 12 European Sustainability Reporting Standards"
            url="#"
            type="Guide"
          />
          <ResourceLink
            title="Double Materiality Assessment"
            description="How to conduct the required materiality assessment"
            url="#"
            type="Guide"
          />
          <ResourceLink
            title="Value Chain Data Collection"
            description="Guidance on collecting upstream and downstream data"
            url="#"
            type="Guide"
          />
          <ResourceLink
            title="Transition Plan Requirements"
            description="E1-1 transition plan disclosure requirements"
            url="#"
            type="Guide"
          />
        </div>
      </div>

      {/* Framework Overview */}
      <div className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Framework Overview</h3>
        
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-[#A0A0A0] mb-2">Description</h4>
          <p className="text-sm text-[#808080] leading-relaxed">
            The Corporate Sustainability Reporting Directive (CSRD) requires companies to report 
            according to the European Sustainability Reporting Standards (ESRS). These standards 
            cover environmental, social, and governance topics with a double materiality perspective, 
            meaning companies must report on both their impact on people and the environment, and 
            how sustainability matters affect the company.
          </p>
        </div>

        {/* Key Concepts */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-[#A0A0A0] mb-3">Key Concepts</h4>
          <div className="space-y-3">
            <ConceptCard
              concept="Double Materiality"
              description="Reporting on both impact materiality (company's impact on environment/society) and financial materiality (how sustainability issues affect the company)"
            />
            <ConceptCard
              concept="Value Chain Coverage"
              description="Disclosure requirements extend to upstream (suppliers) and downstream (customers, end-of-life) impacts"
            />
            <ConceptCard
              concept="Connectivity"
              description="Sustainability information must be connected to and consistent with financial statements"
            />
            <ConceptCard
              concept="Limited Assurance"
              description="Third-party verification required, starting with limited assurance and moving to reasonable assurance by 2028"
            />
          </div>
        </div>

        {/* ESRS Structure */}
        <div>
          <h4 className="text-sm font-semibold text-[#A0A0A0] mb-3">ESRS Structure</h4>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-[#0A0A0A] border border-[#1A1A1A]">
              <p className="text-sm font-medium text-white mb-1">Cross-cutting Standards</p>
              <p className="text-xs text-[#6B6B6B]">ESRS 1 (General Requirements) & ESRS 2 (General Disclosures)</p>
            </div>
            <div className="p-3 rounded-lg bg-[#0A0A0A] border border-[#1A1A1A]">
              <p className="text-sm font-medium text-white mb-1">Environmental (E1-E5)</p>
              <p className="text-xs text-[#6B6B6B]">Climate, Pollution, Water, Biodiversity, Circular Economy</p>
            </div>
            <div className="p-3 rounded-lg bg-[#0A0A0A] border border-[#1A1A1A]">
              <p className="text-sm font-medium text-white mb-1">Social (S1-S4)</p>
              <p className="text-xs text-[#6B6B6B]">Own Workforce, Value Chain Workers, Communities, Consumers</p>
            </div>
            <div className="p-3 rounded-lg bg-[#0A0A0A] border border-[#1A1A1A]">
              <p className="text-sm font-medium text-white mb-1">Governance (G1)</p>
              <p className="text-xs text-[#6B6B6B]">Business Conduct</p>
            </div>
          </div>
        </div>
      </div>

      {/* Penalties & Enforcement */}
      <div className="bg-red-500/5 border border-red-500/30 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <AlertTriangle size={20} className="text-red-400" />
          Penalties & Enforcement
        </h3>

        <div className="mb-4">
          <h4 className="text-sm font-semibold text-[#A0A0A0] mb-2">Non-Compliance Consequences</h4>
          <p className="text-sm text-[#808080]">
            Member States are required to establish penalties for non-compliance with CSRD requirements. 
            These are set at the national level and vary by jurisdiction.
          </p>
        </div>

        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-[#A0A0A0] mb-2">Examples by Jurisdiction:</h4>
          <ul className="space-y-2 text-sm text-[#808080]">
            <li className="flex items-start gap-2">
              <span className="text-[#4A4A4A]">•</span>
              <span><strong className="text-white">Germany:</strong> Up to €10 million or 5% of average annual turnover</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#4A4A4A]">•</span>
              <span><strong className="text-white">France:</strong> Up to €75,000 and potential criminal liability</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#4A4A4A]">•</span>
              <span><strong className="text-white">Netherlands:</strong> Administrative fines and public enforcement actions</span>
            </li>
          </ul>
        </div>

        <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
          <p className="text-sm text-red-300 font-medium">
            Beyond direct penalties, non-compliance may affect access to capital, insurance costs, 
            and business relationships with compliant partners.
          </p>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Implementation Timeline</h3>
        
        <div className="space-y-4">
          <TimelineItem
            year="2024"
            title="Wave 1 - Large PIEs"
            description="Large public-interest entities (>500 employees) reported FY2024; PIEs below the new 1,000-employee threshold exit scope under the Omnibus I transition exemption"
            status="active"
          />
          <TimelineItem
            year="2027"
            title="Amended Scope — Omnibus I (Directive (EU) 2026/470)"
            description="Companies with >1,000 employees AND >€450M net turnover report from FY2027 (first reports 2028). The former wave 2 tranche (250+ employees / €50M) was removed — roughly 80% fewer companies in scope"
            status="upcoming"
          />
          <TimelineItem
            year="Voluntary"
            title="Listed SMEs — out of mandatory scope"
            description="Removed from mandatory CSRD by Omnibus I (below the 1,000-employee threshold); the voluntary VSME standard is the recommended reporting route"
            status="future"
          />
          <TimelineItem
            year="2028"
            title="Non-EU Companies"
            description="Third-country companies with significant EU activity (threshold raised to >€450M EU net turnover by Omnibus I)"
            status="future"
          />
        </div>
      </div>
    </div>
  );
}

// Resource Link Component
function ResourceLink({ title, description, url, type }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-between p-4 rounded-lg border border-[#1A1A1A] hover:border-blue-500/30 hover:bg-blue-500/5 transition-colors group"
    >
      <div className="flex items-start gap-3 flex-1">
        <FileText size={18} className="text-[#4A4A4A] shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-white group-hover:text-blue-400 transition-colors">
            {title}
          </p>
          <p className="text-xs text-[#6B6B6B] mt-0.5">{description}</p>
          {type && (
            <span className="inline-block mt-2 px-2 py-0.5 text-[10px] bg-[#1A1A1A] text-[#6B6B6B] rounded">
              {type}
            </span>
          )}
        </div>
      </div>
      <ExternalLink size={16} className="text-[#4A4A4A] group-hover:text-blue-400 transition-colors shrink-0" />
    </a>
  );
}

// Concept Card Component
function ConceptCard({ concept, description }) {
  return (
    <div className="p-3 rounded-lg bg-[#0A0A0A] border border-[#1A1A1A]">
      <p className="text-sm font-medium text-white mb-1">{concept}</p>
      <p className="text-xs text-[#6B6B6B]">{description}</p>
    </div>
  );
}

// Timeline Item Component
function TimelineItem({ year, title, description, status }) {
  const statusColors = {
    active: 'bg-emerald-500 border-emerald-500',
    upcoming: 'bg-amber-500 border-amber-500',
    future: 'bg-[#2A2A2A] border-[#3A3A3A]'
  };

  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className={`w-4 h-4 rounded-full border-2 ${statusColors[status]}`} />
        <div className="w-0.5 h-full bg-[#1A1A1A] mt-2" />
      </div>
      <div className="flex-1 pb-6">
        <div className="flex items-center gap-3 mb-1">
          <span className="text-lg font-bold text-white">{year}</span>
          {status === 'active' && (
            <span className="px-2 py-0.5 text-[10px] font-medium bg-emerald-500/20 text-emerald-400 rounded">
              NOW
            </span>
          )}
        </div>
        <p className="text-sm font-medium text-[#A0A0A0]">{title}</p>
        <p className="text-xs text-[#6B6B6B] mt-1">{description}</p>
      </div>
    </div>
  );
}
